import React from 'react'
import { observer } from 'mobx-react'
import { action } from 'mobx'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { Form, FormGroupContainer, TextInputContainer } from 'components/inputs'
import { masks } from 'utils/input-mask-util'
import Button from 'components/Button'
import { CountryInput } from 'components/inputs/CountryInput'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import {
  areSelectOptionsEqual,
  SelectOption,
} from 'jobs/questions/Select/SelectQuestionViewModel'
import EditBusinessProfileDialogViewModel from './EditBusinessProfileDialogViewModel'
import { US_STATES_AND_TERRITORIES } from 'jobs/info-gathering/editors/us-states-territories'
import {
  InfoGatheringDialogFieldGrid,
  InfoGatheringDialogScrollArea,
  InfoGatheringDialogSubsectionHeader,
} from 'jobs/info-gathering/components/InfoGatheringDialog'

const US_STATE_OPTIONS: SelectOption[] = US_STATES_AND_TERRITORIES.map(
  ([code, label]) => ({ id: code, text: label }),
)

interface Props {
  vm: EditBusinessProfileDialogViewModel
}

const stateItemToString = (item: SelectOption) => item.text

export default observer(function EditBusinessProfileDialog({ vm }: Props) {
  const editor = vm.businessProfileEditorVm
  const businessInfoVm = editor.businessInfoVm
  const contactInfoVm = editor.contactInfoVm
  const physicalAddressVm = contactInfoVm.physicalAddressVm

  const isUS = physicalAddressVm.country === 'US'

  const stateItemSource = React.useMemo(
    () =>
      ItemSource.static<SelectOption>(US_STATE_OPTIONS, (item, text) =>
        item.text.toLowerCase().includes(text.toLowerCase()),
      ),
    [],
  )
  const stateSelectProps = useCustomSelectStateFromItemSource({
    itemSource: stateItemSource,
  })
  const selectedState = US_STATE_OPTIONS.find(
    (o) => o.id === physicalAddressVm.region,
  )

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
      disableCloseOnOutsideClick
      disableCloseOnEsc
    >
      <ModalSheetHeader withCloseButton bordered>
        <ModalSheetTitle emphasized>Fill in business info</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <InfoGatheringDialogScrollArea>
            <Form>
              <InfoGatheringDialogSubsectionHeader>
                Business information
              </InfoGatheringDialogSubsectionHeader>
              <InfoGatheringDialogFieldGrid>
                <TextInputContainer
                  model={businessInfoVm}
                  field="businessName"
                  name="businessName"
                  label="Business name"
                  error={businessInfoVm.validation.getError('businessName')}
                  data-test="edit-business-name"
                />
                <TextInputContainer
                  model={businessInfoVm}
                  field="dateOfIncorporation"
                  name="dateOfIncorporation"
                  label="Date of incorporation"
                  placeholder="MM/DD/YYYY"
                  mask={masks.americanDate}
                  error={businessInfoVm.validation.getError(
                    'dateOfIncorporation',
                  )}
                  data-test="edit-business-date-of-incorporation"
                />
                <TextInputContainer
                  model={businessInfoVm}
                  field="employerIdentificationNumber"
                  name="employerIdentificationNumber"
                  label="Employer identification number (EIN)"
                  placeholder="xx-xxxxxxx"
                  mask={masks.ein}
                  error={businessInfoVm.validation.getError(
                    'employerIdentificationNumber',
                  )}
                  data-test="edit-business-ein"
                />
              </InfoGatheringDialogFieldGrid>

              <InfoGatheringDialogSubsectionHeader bordered>
                Contact information
              </InfoGatheringDialogSubsectionHeader>
              <InfoGatheringDialogFieldGrid>
                <TextInputContainer
                  model={contactInfoVm}
                  field="email"
                  name="email"
                  label="Email address"
                  placeholder="name@example.com"
                  controlType="email"
                  error={contactInfoVm.validation.getError('email')}
                  data-test="edit-business-email"
                />
                <TextInputContainer
                  model={contactInfoVm}
                  field="phoneNumber"
                  name="phoneNumber"
                  label="Phone number"
                  placeholder="(555) 123-4567"
                  controlType="tel"
                  error={contactInfoVm.validation.getError('phoneNumber')}
                  data-test="edit-business-phone"
                />
                <TextInputContainer
                  model={physicalAddressVm}
                  field="addressLine1"
                  name="addressLine1"
                  label="Address line 1"
                  error={physicalAddressVm.validation.getError('addressLine1')}
                  data-test="edit-business-address-line1"
                />
                <TextInputContainer
                  model={physicalAddressVm}
                  field="addressLine2"
                  name="addressLine2"
                  label="Address line 2"
                  sublabel="(optional)"
                  error={physicalAddressVm.validation.getError('addressLine2')}
                  data-test="edit-business-address-line2"
                />
                <TextInputContainer
                  model={physicalAddressVm}
                  field="city"
                  name="city"
                  label="City"
                  error={physicalAddressVm.validation.getError('city')}
                  data-test="edit-business-city"
                />
                <TextInputContainer
                  model={physicalAddressVm}
                  field="postalCode"
                  name="postalCode"
                  label="ZIP / Postal code"
                  error={physicalAddressVm.validation.getError('postalCode')}
                  data-test="edit-business-postal-code"
                />
                {isUS ? (
                  <FormGroupContainer
                    label="State or territory"
                    htmlFor="region"
                    error={physicalAddressVm.validation.getError('region')}
                  >
                    <CustomSelect<SelectOption, false>
                      {...stateSelectProps}
                      multi={false}
                      value={selectedState}
                      itemToString={stateItemToString}
                      areItemsEqual={areSelectOptionsEqual}
                      placeholder="Select state"
                      onChange={action(
                        (selection: SelectOption | undefined) => {
                          physicalAddressVm.region = selection?.id ?? ''
                        },
                      )}
                      data-test="edit-business-region"
                    />
                  </FormGroupContainer>
                ) : (
                  <TextInputContainer
                    model={physicalAddressVm}
                    field="region"
                    name="region"
                    label="Region"
                    error={physicalAddressVm.validation.getError('region')}
                    data-test="edit-business-region"
                  />
                )}
                <FormGroupContainer
                  label="Country"
                  htmlFor="country"
                  error={physicalAddressVm.validation.getError('country')}
                >
                  <CountryInput
                    value={physicalAddressVm.country}
                    onChange={action((code: string) => {
                      physicalAddressVm.country = code
                    })}
                    data-test="edit-business-country"
                  />
                </FormGroupContainer>
              </InfoGatheringDialogFieldGrid>
            </Form>
          </InfoGatheringDialogScrollArea>
        </ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="primary"
            onClick={vm.submit}
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
            data-test="edit-business-submit-button"
          >
            Save & Close
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
